import React, { FunctionComponent, useState } from 'react'
import { Col, Container, Row } from 'react-grid-system'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'react-tippy'

import { getRandomColor } from './PatientDetail.model'
import {
  StyledPatientHeaderButtonContainer,
  StyledPatientHeaderItemAvatar,
  StyledPatientHeaderItems,
  StyledPatientHeaderItemsWrapper
} from './PatientDetail.styles'
import { PatientInitialsAvatar } from '../PatientInitialsAvatar/PatientInitialsAvatar'
import { ClinTheme } from '../../../ClinTheme'
import { ClinButton } from '../../../components/ClinButton'
import { ClinText } from '../../../components/ClinText'
import { PatientStatus } from '../../../constants'
import {
  isPatientDetailPostTrialAccess,
  resolveCountryProgramStatusForInstitute,
  shouldShowPatientDetailHeaderAvatar
} from '../patientInitialsDisplay'
import { shouldDisablePtaBeginOrder } from './ptaBeginOrderGate'
import { hasResupplyBlockingOrder } from './resupplyOrderInProgress'
import {
  OrgAddressDto,
  PatientDetailsDto,
  ProgramCatalogDto
} from '../../../types/swaggerTypes'
import { StyledHeader } from '../../Programs/ProgramDetail/ProgramDetail.styles'
import {
  OPAOrderTypes,
  PatientOrderStatus,
  normalizePatientOrderStatus,
  patientOrderStatusTooltipTranslation
} from '../PatientDashboard/Patient.model'

interface IPatientDetailHeaderProps {
  patient: PatientDetailsDto
  programCatalog?: ProgramCatalogDto
  defaultShippingAddress?: OrgAddressDto | null
  instituteCountryCode?: string | null
  /** When getProgress has confirmed baseline complete locally (CLOS-13424). */
  isBaselineProgressConfirmed?: boolean
  /** If the program is closed displaying an appropriated banner */
  isProgramClosed: boolean
  /** Details CTA title */
  primaryCTAButtonText: string
  /** Details CTA tooltip */
  primaryCTAButtonTooltip: string
  /** Spinner on the CTA while its check runs; also blocks a second click */
  isPrimaryCTALoading?: boolean
  /** Called when the user click on "Primary CTA" */
  handlePrimaryCTA?: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** Called when user open  form */
  handleShowTransferForm?: () => void
  /** Discontinue from program */
  handleDiscontinueFromProgram?: () => void
  /** Allows discontinuation */
  allowsDiscontinuation: boolean
}

export const PatientDetailHeader: FunctionComponent<
  IPatientDetailHeaderProps
> = ({
  patient,
  programCatalog,
  defaultShippingAddress,
  instituteCountryCode,
  isProgramClosed,
  primaryCTAButtonText,
  isPrimaryCTALoading = false,
  handleShowTransferForm,
  handlePrimaryCTA,
  handleDiscontinueFromProgram,
  allowsDiscontinuation,
  isBaselineProgressConfirmed = false
}) => {
  const { t } = useTranslation()
  const [avatarColor] = useState(getRandomColor())

  const normalizedOrderStatus = normalizePatientOrderStatus(
    patient.patientOrderStatus
  ) as PatientOrderStatus

  // AwaitingBaselineData normally hides/disables the primary CTA. After
  // getProgress confirms baseline (CLOS-13424), treat it as order-eligible so
  // Begin Order can render before Oracle/BFF flips status (CLOS-13651/13667).
  const ineligibleStatuses = [
    PatientOrderStatus.PatientDiscontinued,
    PatientOrderStatus.UnderMedicalReview,
    PatientOrderStatus.ProgramClosed,
    PatientOrderStatus.Submitted,
    PatientOrderStatus.HealthAuthorityReview,
    PatientOrderStatus.UnderReview,
    PatientOrderStatus.AwaitingConsent,
    ...(isBaselineProgressConfirmed
      ? []
      : [PatientOrderStatus.AwaitingBaselineData])
  ]

  const isPatientForResupply =
    (normalizedOrderStatus === PatientOrderStatus.MedicalReviewDeclined ||
      normalizedOrderStatus === PatientOrderStatus.HealthAuthorityDeclined) &&
    patient.orderType !== OPAOrderTypes.Resupply

  const isPatientUneligible =
    !normalizedOrderStatus ||
    ineligibleStatuses.includes(normalizedOrderStatus) ||
    (normalizedOrderStatus === PatientOrderStatus.Processing &&
      patient.trackingLink === 'N/A') ||
    isPatientForResupply

  const isDiscontinueButtonDisabled =
    normalizedOrderStatus === PatientOrderStatus.MedicalReviewDeclined ||
    normalizedOrderStatus === PatientOrderStatus.HealthAuthorityReview ||
    normalizedOrderStatus === PatientOrderStatus.HealthAuthorityDeclined ||
    normalizedOrderStatus === PatientOrderStatus.Submitted ||
    patient.patientStatus.toUpperCase() ===
      PatientStatus.DISCONTINUED.toUpperCase() ||
    !allowsDiscontinuation

  const isPostTrialAccessPatient = isPatientDetailPostTrialAccess(
    patient.program?.status,
    programCatalog,
    defaultShippingAddress,
    instituteCountryCode
  )

  // CLOS-13994 — Transfer/Discontinue are not offered for Post-Trial Access Only.
  const isTransferButtonDisabled =
    isProgramClosed ||
    normalizedOrderStatus === PatientOrderStatus.MedicalReviewDeclined ||
    normalizedOrderStatus === PatientOrderStatus.HealthAuthorityDeclined

  const isPtaBeginOrderBlocked = shouldDisablePtaBeginOrder(
    patient,
    isPostTrialAccessPatient,
    isBaselineProgressConfirmed
  )

  const isResupplyBlockedByOpenOrder = hasResupplyBlockingOrder(
    normalizedOrderStatus,
    patient.pastOrders
  )

  const primaryCTATooltipKey = isResupplyBlockedByOpenOrder
    ? 'patient_detail:patient_status_tooltip.order_in_progress'
    : patientOrderStatusTooltipTranslation[
        normalizedOrderStatus as keyof typeof patientOrderStatusTooltipTranslation
      ]

  const isPrimaryCTAButtonDisabled =
    isPatientUneligible ||
    isResupplyBlockedByOpenOrder ||
    isProgramClosed ||
    primaryCTAButtonText === '' ||
    isPtaBeginOrderBlocked

  const countryProgramStatus = resolveCountryProgramStatusForInstitute(
    programCatalog,
    defaultShippingAddress,
    instituteCountryCode
  )

  return (
    <StyledHeader>
      <Container>
        <Row>
          <Col md={9} lg={9}>
            <StyledPatientHeaderItemsWrapper>
              {shouldShowPatientDetailHeaderAvatar(
                patient.initials,
                patient.program?.status,
                countryProgramStatus
              ) && (
                <StyledPatientHeaderItems>
                  <StyledPatientHeaderItemAvatar>
                    <PatientInitialsAvatar
                      initials={patient.initials}
                      background={avatarColor}
                      size="md"
                    />

                    <ClinText
                      fontSize={ClinTheme.fontSizes[4]}
                      fontWeight={ClinTheme.fontWeights.medium}
                      lineHeight={ClinTheme.lineHeights.largeParagraph}
                    >
                      {patient.patientNumber}
                    </ClinText>
                  </StyledPatientHeaderItemAvatar>

                  <StyledPatientHeaderButtonContainer>
                    {!isPostTrialAccessPatient && (
                      <Tooltip
                        theme="light"
                        position="bottom"
                        trigger="mouseenter"
                        arrow={true}
                        title={t('patient_detail:discontinue_title')}
                      >
                        <ClinButton
                          className="discontinue-button"
                          variant="purpleLightSecondary"
                          disabled={isDiscontinueButtonDisabled}
                          onClick={handleDiscontinueFromProgram}
                        >
                          {t('patient_detail:button_discontinue')}
                        </ClinButton>
                      </Tooltip>
                    )}

                    {!isPostTrialAccessPatient && (
                      <Tooltip
                        theme="light"
                        position="bottom"
                        trigger="mouseenter"
                        arrow={true}
                        title={t('patient_detail:title_transfer_column')}
                      >
                        <span style={{ display: 'inline-block' }}>
                          <ClinButton
                            className="transfer-button"
                            variant="purpleLightSecondary"
                            disabled={isTransferButtonDisabled}
                            onClick={handleShowTransferForm}
                          >
                            {t('patient_detail:button_transfer')}
                          </ClinButton>
                        </span>
                      </Tooltip>
                    )}

                    {primaryCTAButtonText && (
                      <Tooltip
                        theme="light"
                        position="bottom"
                        trigger="mouseenter"
                        arrow={true}
                        disabled={!isPatientForResupply}
                        title={
                          primaryCTATooltipKey ? t(primaryCTATooltipKey) : ''
                        }
                      >
                        <ClinButton
                          className="resupply-button"
                          variant="purpleLight"
                          disabled={
                            isPrimaryCTAButtonDisabled || isPrimaryCTALoading
                          }
                          isLoading={isPrimaryCTALoading}
                          onClick={handlePrimaryCTA}
                        >
                          {primaryCTAButtonText}
                        </ClinButton>
                      </Tooltip>
                    )}
                  </StyledPatientHeaderButtonContainer>
                </StyledPatientHeaderItems>
              )}
            </StyledPatientHeaderItemsWrapper>
          </Col>
          <Col md={3} lg={3}></Col>
        </Row>
      </Container>
    </StyledHeader>
  )
}
